import { type MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import {
  clearAttachmentVersionRegistry,
  registerAttachmentFilename,
  registerAttachmentVersion,
} from "@/components/chat/documentVersionEvents";
import { featureFlags } from "@/config/featureFlags";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { useNotificationEvent } from "@/context/NotificationContext";
import { useSharedSessions } from "@/context/SharedSessionsContext";
import { buildNewChatPath } from "@/lib/chatNavigation";
import { agentService, chatService } from "@/lib/services";
import type { Agent, ChatMessage, ChatSession, ReasoningStep, SharedSessionInfo, ToolCallInfo } from "@/types";

/**
 * Parameters accepted by `useChatSession`.
 */
interface UseChatSessionOpts {
  /** The session UUID from the route (undefined when starting fresh). */
  sessionId: string | undefined;
  /** Initial project UUID from the route (avoids a flash of global agents). */
  initialProjectUuid?: string | null;
  /** Router navigate function. */
  navigate: (path: string, opts?: { replace?: boolean }) => void;
  /** Set shared-session info when loading a session owned by someone else. */
  setSharedInfo: React.Dispatch<React.SetStateAction<SharedSessionInfo | null>>;
  /** Reset attachment state when starting a new chat. */
  resetAttachments: () => void;
  /**
   * Disconnect the active stream and clear streaming UI state.
   * Called when the user navigates to a different session mid-stream.
   */
  disconnectStream: () => void;
  /**
   * Guard ref — when either flag is true AND the sessionId matches the
   * current session, the sessionId-change effect will skip reloading
   * (the URL change was triggered by our own code, not the user).
   * Passed as a ref so that changes are visible without re-triggering the effect.
   */
  guardsRef: MutableRefObject<{
    isStreaming: boolean;
    isDownloadingFiles: boolean;
    askUserActive: boolean;
  }>;
}

/**
 * Encapsulates all session-related state and data-loading logic extracted
 * from Chat.tsx.
 *
 * Owns: `selectedModel`, `selectedAgent`, `agents`,
 *       `currentSession`, `messages`, `inputValue`,
 *       `isLoading`, `modelSupportsVision`.
 *
 * Manages: `loadAgents()`, `loadSession()`, `startNewChat()`,
 *          and the effects that trigger them.
 */

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Groups intermediate assistant (tool-call) and tool (result) messages as
 * `reasoningSteps` on the next final assistant message.
 *
 * Messages that don't belong to a reasoning chain (user, system, and final
 * assistant messages) are passed through as-is.
 */
/** Convert accumulated tool calls + results into ReasoningStep[] for display. */
export function buildReasoningSteps(toolCalls: ToolCallInfo[], toolResults: Map<string, ChatMessage>): ReasoningStep[] {
  return toolCalls.map((tc) => {
    const toolResult = toolResults.get(tc.id);
    return {
      toolCallId: tc.id,
      toolName: tc.function.name,
      toolArgs: tc.function.arguments,
      resultContent: toolResult?.content,
      toolDisplayName: (toolResult?.messageMetadata?.tool_name as string) || tc.function.name,
    };
  });
}

export function groupReasoningSteps(messages: ChatMessage[]): ChatMessage[] {
  // NOSONAR -- message grouping requires per-role conditional accumulation
  const result: ChatMessage[] = [];
  let pendingToolCalls: ToolCallInfo[] = [];
  let pendingToolResults: Map<string, ChatMessage> = new Map();

  for (const msg of messages) {
    const isIntermediate = msg.role === "assistant" && msg.messageMetadata?.intermediate;
    const isTool = msg.role === "tool";

    if (isIntermediate && msg.toolCalls) {
      for (const tc of msg.toolCalls) {
        pendingToolCalls.push(tc);
      }
      continue;
    }

    if (isTool && msg.toolCallId) {
      pendingToolResults.set(msg.toolCallId, msg);
      continue;
    }

    // Visible message — attach accumulated reasoning steps if applicable
    if (msg.role === "assistant" && pendingToolCalls.length > 0) {
      const steps = buildReasoningSteps(pendingToolCalls, pendingToolResults);
      result.push({ ...msg, reasoningSteps: steps });
    } else {
      result.push(msg);
    }
    pendingToolCalls = [];
    pendingToolResults = new Map();
  }

  return result;
}

export function useChatSession(opts: UseChatSessionOpts) {
  // NOSONAR -- session management hook with load/navigation/shared-session logic
  const { sessionId, initialProjectUuid, navigate, setSharedInfo, resetAttachments, disconnectStream, guardsRef } =
    opts;
  const { enabledModels, defaultModelId, tierDefaults } = useApp();
  const { profile } = useAuth();
  const { sharedSessions } = useSharedSessions();

  // Use a ref for profile so loadSession's useCallback doesn't need it as a
  // dependency (which would cause the session-load effect to refire on every
  // render since profile is a new object reference each render).
  const profileRef = useRef(profile);
  useEffect(() => {
    profileRef.current = profile;
  }, [profile]);

  // Use a ref for sharedSessions so loadSession's useCallback doesn't need
  // it as a dependency — sharedSessions gets a new array identity whenever
  // the SharedSessionsProvider refetches (on mount, cache TTL expiry, etc.),
  // which would recreate loadSession and re-trigger the session-load effect,
  // causing an unwanted session reload that wipes the ask-user card and
  // in-memory messages.
  const sharedSessionsRef = useRef(sharedSessions);
  useEffect(() => {
    sharedSessionsRef.current = sharedSessions;
  }, [sharedSessions]);

  // -----------------------------------------------------------------------
  // State
  // -----------------------------------------------------------------------
  const [selectedModel, setSelectedModel] = useState(defaultModelId || "");
  const appliedDefaultRef = useRef<string | null>(defaultModelId);
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  // "Auto" in the agent picker (MHS-627): on for every new chat; only takes effect with the flag and an agent to pick.
  const [autoRoute, setAutoRoute] = useState(true);
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null);
  // Per-chat reasoning effort (MHS-669); null = provider default. Editable mid-chat, unlike model/brand.
  const [selectedReasoningEffort, setSelectedReasoningEffort] = useState<string | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  // Agents that are NOT attached to the current project. Only populated when
  // inside a project so the picker can optionally offer general-purpose agents
  // (e.g. a PPTX builder) alongside the project's own agents.
  const [nonProjectAgents, setNonProjectAgents] = useState<Agent[]>([]);
  const [projectUuid, setProjectUuid] = useState<string | null>(initialProjectUuid ?? null);
  const [currentSession, setCurrentSession] = useState<ChatSession | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Derived -------------------------------------------------------------------
  // When a tier-based default is selected (e.g. "tier:fast"), resolve to the
  // underlying model so we can check its capabilities (vision, reasoning, etc.).
  const resolvedModelId = selectedModel.startsWith("tier:")
    ? (tierDefaults.find((td) => `tier:${td.tier}` === selectedModel)?.modelId ?? selectedModel)
    : selectedModel;
  const selectedModelInfo = enabledModels.find((m) => m.id === resolvedModelId);
  const modelSupportsVision = selectedModelInfo?.supportsVision ?? false;
  const autoRouteAvailable = featureFlags.agentRouting && agents.length > 0;
  const isAutoRoute = autoRouteAvailable && autoRoute && !selectedAgent && !currentSession;

  // Track current session UUID via ref so the sessionId effect can read the
  // latest value without depending on (and re-firing for) currentSession state.
  const currentSessionUuidRef = useRef<string | null>(null);
  currentSessionUuidRef.current = currentSession?.uuid ?? null;

  // -----------------------------------------------------------------------
  // Data loaders
  // -----------------------------------------------------------------------
  const loadAgents = useCallback(async (forProjectUuid: string | null) => {
    try {
      const response = await agentService.list({
        pageSize: 100,
        projectUuid: forProjectUuid ?? undefined,
        excludeProjectAgents: !forProjectUuid,
      });
      const agentList = response.data || [];
      setAgents(agentList);

      // Auto-select the agent when inside a project with exactly one agent
      if (forProjectUuid && agentList.length === 1) {
        setSelectedAgent(agentList[0].uuid);
      }

      // When inside a project, also fetch general-purpose (non-project) agents
      // so the picker can offer them behind a toggle. Outside a project the
      // main list already contains all accessible non-project agents.
      if (forProjectUuid) {
        const nonProjectResponse = await agentService.list({
          pageSize: 100,
          excludeProjectAgents: true,
        });
        setNonProjectAgents(nonProjectResponse.data || []);
      } else {
        setNonProjectAgents([]);
      }
    } catch (err) {
      console.error("Failed to load agents:", err);
    }
  }, []);

  const loadSession = useCallback(
    async (id: string) => {
      try {
        setIsLoading(true);
        setSharedInfo(null);
        clearAttachmentVersionRegistry();
        const [sessionRes, messagesRes] = await Promise.all([chatService.getSession(id), chatService.getMessages(id)]);
        setCurrentSession(sessionRes.data);
        // Group intermediate tool messages into reasoning steps on the
        // final assistant message rather than discarding them entirely.
        const allMessages: ChatMessage[] = messagesRes.data;
        const visibleMessages = groupReasoningSteps(allMessages);

        // Populate the attachment version registry from the documents API
        // BEFORE setting messages, so the Markdown link override can show
        // version badges on the initial render.
        //
        // Note: We use the documents endpoint (which queries by session_id)
        // rather than message attachments, because tool-generated attachments
        // don't have message_id set and won't appear in the messages response.
        const sessionUuid = sessionRes.data.uuid;
        try {
          const docsRes = await chatService.getSessionDocuments(id);
          for (const lineage of docsRes.data) {
            for (const ver of lineage.versions) {
              registerAttachmentVersion(ver.attachmentUuid, {
                version: ver.version,
                lineageUuid: lineage.uuid,
                isRevision: ver.version > 1,
                sessionUuid,
              });
            }
          }
        } catch {
          // Non-critical — version badges won't show but chat still works
          console.warn("Failed to load document lineages for session", id);
        }

        // Register attachment UUID -> real filename so the Markdown link
        // renderer can decide previewability from the actual file (e.g. a PDF)
        // even when the LLM wrote prose link text like "Open PDF preview".
        for (const msg of allMessages) {
          for (const att of msg.attachments ?? []) {
            registerAttachmentFilename(att.uuid, att.filename);
          }
        }

        setMessages(visibleMessages);
        if (sessionRes.data.modelId) {
          setSelectedModel(sessionRes.data.modelId);
        }
        if (sessionRes.data.agentUuid) {
          setSelectedAgent(sessionRes.data.agentUuid);
        }
        setSelectedBrand(sessionRes.data.brandUuid ?? null);
        setSelectedReasoningEffort(sessionRes.data.reasoningEffort ?? null);
        // Restore project link if session was scoped to a project — always
        // sets (including clearing to null) so a session with no project
        // never inherits a stale value left over from a previously-loaded
        // session.
        setProjectUuid(sessionRes.data.projectUuid || null);
        // Check if this is a shared session (not owned by the current user).
        // Compare by UUID since the API returns userUuid on the session.
        // Use profileRef (not profile directly) to avoid making this callback
        // depend on profile and causing infinite re-loads on re-render.
        const currentUserUuid = profileRef.current?.user.uuid;
        if (currentUserUuid && sessionRes.data.userUuid !== currentUserUuid) {
          const sharedSession = sharedSessionsRef.current.find((s) => s.uuid === sessionRes.data.uuid);
          if (sharedSession) {
            setSharedInfo(sharedSession.sharing);
          }
        }
      } catch (err) {
        console.error("Failed to load session:", err);
        navigate("/chat");
      } finally {
        setIsLoading(false);
      }
    },
    [navigate, setSharedInfo],
  );

  /**
   * Reset chat state and start fresh. When `keepProjectUuid` is passed (the
   * user is currently in a project-scoped chat), the new chat stays nested
   * under that project instead of dropping the context silently — pass
   * `null`/omit to leave the project and start a fully blank chat.
   */
  const startNewChat = useCallback(
    (keepProjectUuid?: string | null) => {
      // A first message still on its way (e.g. waiting on the auto-router) has no chat URL yet,
      // so the sessionId effect below cannot see this navigation: stop it here.
      if (guardsRef.current.isStreaming) {
        disconnectStream();
      }
      setCurrentSession(null);
      setMessages([]);
      setInputValue("");
      resetAttachments();
      setSelectedAgent(null);
      setSelectedReasoningEffort(null);
      setAutoRoute(true);
      setProjectUuid(keepProjectUuid ?? null);
      setSharedInfo(null);
      clearAttachmentVersionRegistry();
      navigate(buildNewChatPath(keepProjectUuid));
    },
    [navigate, resetAttachments, setSharedInfo, disconnectStream],
  );

  /** Picker choice of an agent, or "No Agent" with null: turns Auto off. */
  const selectAgent = useCallback((uuid: string | null) => {
    setSelectedAgent(uuid);
    setAutoRoute(false);
  }, []);

  /** Picker change: keep it, mirror it into the open chat and save it there (a new chat sends it on create). */
  const setReasoningEffort = useCallback((effort: string | null) => {
    setSelectedReasoningEffort(effort);
    const uuid = currentSessionUuidRef.current;
    if (!uuid) return;
    setCurrentSession((prev) => (prev ? { ...prev, reasoningEffort: effort } : prev));
    void chatService.updateSession(uuid, { reasoningEffort: effort }).catch((err) => {
      console.error("Failed to save reasoning effort:", err);
    });
  }, []);

  /** Picker choice of "Auto". */
  const selectAutoRoute = useCallback(() => {
    setSelectedAgent(null);
    setAutoRoute(true);
  }, []);

  // -----------------------------------------------------------------------
  // Effects
  // -----------------------------------------------------------------------

  // Load agents on mount, and reload whenever the project scope changes so
  // the agent picker only ever offers agents relevant to the current
  // context (all accessible agents outside a project; only that project's
  // agents while inside one).
  useEffect(() => {
    loadAgents(projectUuid);
  }, [loadAgents, projectUuid]);

  // Load session when sessionId changes
  useEffect(() => {
    if (sessionId) {
      // Don't reload the session if we're actively streaming or downloading
      // AND the sessionId matches the current session — this means WE just
      // created or forked this session ourselves and the navigate() was only
      // to update the URL bar.  When the new sessionId is *different* from
      // the current session, the user clicked a different chat and we must
      // honour that navigation even if a stream is still running.
      const isGuarded =
        guardsRef.current.isStreaming || guardsRef.current.isDownloadingFiles || guardsRef.current.askUserActive;
      const isSameSession = currentSessionUuidRef.current === sessionId;

      if (isGuarded && isSameSession) return;

      // If we're navigating to a different session while streaming, disconnect
      // the active stream so it doesn't keep appending to the wrong session.
      if (isGuarded && !isSameSession) {
        disconnectStream();
      }

      loadSession(sessionId);
    } else {
      // Navigating to /chat (new chat) — disconnect any active stream
      // so it doesn't create phantom operations or duplicate messages.
      if (guardsRef.current.isStreaming) {
        disconnectStream();
      }
      setCurrentSession(null);
      setMessages([]);
      setSelectedReasoningEffort(null);
      setSelectedModel(appliedDefaultRef.current ?? "");
    }
  }, [sessionId, loadSession]);

  useNotificationEvent("chat_title_updated", ({ session_uuid, title }) => {
    setCurrentSession((prev) => (prev?.uuid === session_uuid ? { ...prev, title } : prev));
  });

  // Set default model when models load
  useEffect(() => {
    if (!defaultModelId) return;
    const previousDefault = appliedDefaultRef.current;
    appliedDefaultRef.current = defaultModelId;
    if (!selectedModel || (!sessionId && selectedModel === previousDefault)) {
      setSelectedModel(defaultModelId);
    }
  }, [defaultModelId, selectedModel, sessionId]);

  // -----------------------------------------------------------------------
  // Stable callback for streaming hook (avoids stale closures)
  // -----------------------------------------------------------------------
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const getMessages = useCallback(() => messagesRef.current, []);

  return {
    // State
    selectedModel,
    setSelectedModel,
    selectedAgent,
    setSelectedAgent,
    setAutoRoute,
    selectAgent,
    selectAutoRoute,
    selectedBrand,
    setSelectedBrand,
    selectedReasoningEffort,
    setReasoningEffort,
    agents,
    nonProjectAgents,
    currentSession,
    setCurrentSession,
    messages,
    setMessages,
    inputValue,
    setInputValue,
    isLoading,
    projectUuid,
    setProjectUuid,
    // Derived
    selectedModelInfo,
    modelSupportsVision,
    autoRouteAvailable,
    isAutoRoute,
    // Callbacks
    startNewChat,
    getMessages,
  };
}
